import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader,
  DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

const SEVERITIES = ["Critical", "High", "Medium", "Low"];

/**
 * Reusable "Create manual alert / reminder" dialog.
 *
 * Two modes:
 *  - `documentId` prop provided → doc is hidden and pre-filled (Analysis page banner)
 *  - `documentId` absent → optional document dropdown (Alerts page global button)
 *
 * On success: toast + onCreated(alert) callback (parent refreshes its list).
 */
export default function CreateAlertDialog({
  open, onClose, onCreated,
  documentId = null,
  workspaceDocuments = null,   // [{id, filename, document_type}] when doc select needed
  variant = "reminder",         // "reminder" (banner) | "alert" (global)
}) {
  const { t } = useTranslation("components");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [severity, setSeverity] = useState("Medium");
  const [dueAt, setDueAt] = useState("");
  const [linkedDocId, setLinkedDocId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset form each time the dialog opens
  useEffect(() => {
    if (open) {
      setTitle("");
      setSummary("");
      setSeverity("Medium");
      setDueAt("");
      setLinkedDocId("");
      setError(null);
    }
  }, [open]);

  const isReminder = variant === "reminder";
  const scope = isReminder ? "create_reminder" : "create_alert";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const trimmedTitle = title.trim();
    const trimmedSummary = summary.trim();
    if (trimmedTitle.length < 3) {
      setError(t(`${scope}.error_title_short`));
      return;
    }
    if (trimmedSummary.length < 3) {
      setError(t(`${scope}.error_summary_short`));
      return;
    }
    const payload = {
      title: trimmedTitle,
      summary: trimmedSummary,
      severity,
    };
    const linkedId = documentId || (linkedDocId || null);
    if (linkedId) payload.document_id = linkedId;
    if (dueAt) {
      // Coerce YYYY-MM-DD to ISO8601 at end-of-day UTC
      try {
        const d = new Date(`${dueAt}T23:59:59.000Z`);
        payload.due_at = d.toISOString();
      } catch {
        setError(t(`${scope}.error_due_at_invalid`));
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await api.post("/alm/alerts", payload);
      toast.success(t(`${scope}.success`, { title: trimmedTitle }));
      onCreated?.(res.data);
      onClose?.();
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) || t(`${scope}.error_generic`),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const testIdRoot = isReminder ? "create-reminder-dialog" : "create-alert-dialog";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid={testIdRoot}>
        <DialogHeader>
          <DialogTitle data-testid={`${testIdRoot}-title`}>
            {t(`${scope}.title`)}
          </DialogTitle>
          <DialogDescription>{t(`${scope}.subtitle`)}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`${testIdRoot}-title-input`} className="text-xs">
              {t(`${scope}.field_title`)}
            </Label>
            <Input
              id={`${testIdRoot}-title-input`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t(`${scope}.field_title_placeholder`)}
              autoComplete="off"
              maxLength={200}
              data-testid={`${testIdRoot}-title-input`}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`${testIdRoot}-summary-input`} className="text-xs">
              {t(`${scope}.field_summary`)}
            </Label>
            <Textarea
              id={`${testIdRoot}-summary-input`}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={t(`${scope}.field_summary_placeholder`)}
              rows={4}
              maxLength={2000}
              data-testid={`${testIdRoot}-summary-input`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t(`${scope}.field_severity`)}</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger data-testid={`${testIdRoot}-severity-trigger`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`${testIdRoot}-severity-option-${s.toLowerCase()}`}
                    >
                      {t(`${scope}.severity_${s.toLowerCase()}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${testIdRoot}-due-input`} className="text-xs">
                {t(`${scope}.field_due_at`)}
              </Label>
              <Input
                id={`${testIdRoot}-due-input`}
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                data-testid={`${testIdRoot}-due-input`}
              />
            </div>
          </div>

          {!documentId && workspaceDocuments && (
            <div className="space-y-1.5">
              <Label className="text-xs">{t(`${scope}.field_document_optional`)}</Label>
              <Select
                value={linkedDocId || "__none__"}
                onValueChange={(v) => setLinkedDocId(v === "__none__" ? "" : v)}
              >
                <SelectTrigger data-testid={`${testIdRoot}-document-trigger`}>
                  <SelectValue placeholder={t(`${scope}.field_document_placeholder`)} />
                </SelectTrigger>
                <SelectContent className="max-h-72 overflow-y-auto">
                  <SelectItem value="__none__" data-testid={`${testIdRoot}-document-option-none`}>
                    {t(`${scope}.field_document_none`)}
                  </SelectItem>
                  {workspaceDocuments.map((d) => (
                    <SelectItem
                      key={d.id}
                      value={d.id}
                      data-testid={`${testIdRoot}-document-option-${d.id}`}
                    >
                      {d.filename}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {documentId && (
            <input
              type="hidden"
              value={documentId}
              readOnly
              data-testid={`${testIdRoot}-hidden-document-id`}
            />
          )}

          {error && (
            <div
              className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
              data-testid={`${testIdRoot}-error`}
            >
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid={`${testIdRoot}-cancel`}
            >
              {t(`${scope}.cancel`)}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid={`${testIdRoot}-submit`}
            >
              {submitting ? t(`${scope}.submitting`) : t(`${scope}.submit`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
